import {
  createContext,
  useContext,
  type ReactNode,
} from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import AuthApi from '../apis/AuthApi';
import { useUserDetails } from '../helpers/UserContext';

const SUBSCRIPTION_CACHE_TIME = 24 * 60 * 60 * 1000;

export type UserSubscriptions = Awaited<
  ReturnType<typeof AuthApi.getUserSubscriptions>
>;

export const subscriptionKeys = {
  all: ['userSubscriptions'] as const,
  forSession: (userId: string, sessionExpiresAt: number) =>
    [...subscriptionKeys.all, userId, sessionExpiresAt] as const,
};

type UserSubscriptionsContextValue = UseQueryResult<UserSubscriptions, Error>;

const UserSubscriptionsContext =
  createContext<UserSubscriptionsContextValue | null>(null);

/** Fetches and caches the current user's subscription when the app starts. */
export function UserSubscriptionsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [userDetails, , , hasAuth] = useUserDetails();
  const isAuthenticated = hasAuth();
  const userId = userDetails?.userId;
  const sessionExpiresAt = userDetails?.exp;

  const subscriptions = useQuery({
    queryKey:
      userId && sessionExpiresAt
        ? subscriptionKeys.forSession(userId, sessionExpiresAt)
        : subscriptionKeys.all,
    queryFn: () => AuthApi.getUserSubscriptions(),
    enabled: isAuthenticated && !!userId && !!sessionExpiresAt,
    staleTime: SUBSCRIPTION_CACHE_TIME,
  });

  return (
    <UserSubscriptionsContext.Provider value={subscriptions}>
      {children}
    </UserSubscriptionsContext.Provider>
  );
}

/** Read the cached subscription state from any component under the provider. */
export function useUserSubscriptions() {
  const context = useContext(UserSubscriptionsContext);
  if (!context) {
    throw new Error(
      'useUserSubscriptions must be used within a UserSubscriptionsProvider',
    );
  }
  return context;
}
